import {
    autoUpdate,
    flip,
    offset,
    shift,
    useFloating,
} from "@floating-ui/react";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
    faCheck,
    faChevronDown,
    faCopy,
    faCrosshairs,
    faMap,
    faTag,
    faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popover } from "@headlessui/react";
import { useQuery } from "@tanstack/react-query";
import { useCopyToClipboard } from "@uidotdev/usehooks";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useGeoDataApi } from "../../hooks";
import { ROUTES } from "../../routes";

export const locationColumnModeAtom = atomWithStorage<
    "COORDINATES" | "PRESETS"
>("locationColumn", "PRESETS");

export const LocationColumnModeToggle = () => {
    const [locationColumnMode, setLocationColumnMode] = useAtom(
        locationColumnModeAtom,
    );

    const buttonClass = (active: boolean) => `
        w-7 py-1 grid place-items-center
        ${active ? "bg-[#1f2d6b] text-white" : "bg-white text-[#6b7699] hover:text-[#1f2d6b]"}
    `;

    return (
        <div className="flex border border-[#d8dde8] rounded overflow-hidden">
            <button
                type="button"
                title="Show Presets"
                className={buttonClass(locationColumnMode === "PRESETS")}
                onClick={(e) => {
                    e.stopPropagation();
                    setLocationColumnMode("PRESETS");
                }}
            >
                <FontAwesomeIcon icon={faTag} className="h-3" />
            </button>
            <button
                type="button"
                title="Show Coordinates"
                className={`border-l border-[#d8dde8] ${buttonClass(locationColumnMode === "COORDINATES")}`}
                onClick={(e) => {
                    e.stopPropagation();
                    setLocationColumnMode("COORDINATES");
                }}
            >
                <FontAwesomeIcon icon={faCrosshairs} className="h-3" />
            </button>
        </div>
    );
};

const formatCoordinates = (location: number[]) =>
    `${location[1].toFixed(4)}, ${location[0].toFixed(4)}`;

const IconButton = (props: {
    title: string;
    icon: IconDefinition;
    iconClassName?: string;
    onClick: () => void;
}) => (
    <button
        type="button"
        title={props.title}
        className="flex-none w-[26px] h-[26px] rounded grid place-items-center text-[#1f2d6b] hover:bg-[#e6eaf4]"
        onClick={(e) => {
            e.stopPropagation();
            props.onClick();
        }}
    >
        <FontAwesomeIcon
            icon={props.icon}
            className={`h-3 w-3 ${props.iconClassName ?? ""}`}
        />
    </button>
);

const CopyButton = (props: { location: number[] }) => {
    const [, copy] = useCopyToClipboard();
    const [copied, setCopied] = useState(false);
    const timeout = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => () => clearTimeout(timeout.current), []);

    return (
        <IconButton
            title="Copy coordinates"
            icon={copied ? faCheck : faCopy}
            iconClassName={copied ? "text-[#1e8bc3]" : undefined}
            onClick={() => {
                copy(`${props.location[1]}, ${props.location[0]}`);
                setCopied(true);
                clearTimeout(timeout.current);
                timeout.current = setTimeout(() => setCopied(false), 1400);
            }}
        />
    );
};

export const PresetPill = (props: {
    name: string;
    selected?: boolean;
    truncate?: boolean;
}) => (
    <span
        title={props.name}
        className={`
            h-6 px-2 rounded-full border text-[13px] inline-flex items-center box-border min-w-0
            ${props.truncate ? "max-w-[130px]" : "max-w-full"}
            ${props.selected ? "bg-[#eef1f8] border-[#1f2d6b] text-[#1f2d6b] font-medium" : "border-[#cfd6e4] text-[#1d2b5e]"}
        `}
    >
        <span className="block min-w-0 whitespace-nowrap overflow-hidden text-ellipsis">
            {props.name}
        </span>
    </span>
);

/**
 * Resolves preset ids to their names, with the selected ones first.
 * Presets not found (e.g. not accessible by the user) are dropped.
 */
export const useLocationPresets = (
    presetIds?: number[],
    selectedPresets?: number[],
) => {
    const geoDataApiClient = useGeoDataApi();

    // Just fetch all presets, this is metadata only, not the shapes.
    const locationPresetsQuery = useQuery({
        queryKey: ["geoFilterItems"],
        queryFn: async () => {
            const response = await geoDataApiClient.geodataItemsList({
                pageSize: 1000,
            });
            return response.results;
        },
        enabled: !!presetIds,
        staleTime: Infinity,
    });

    return useMemo(() => {
        if (!presetIds || !locationPresetsQuery.data) {
            return [];
        }
        const selected = new Set(selectedPresets ?? []);
        return presetIds
            .map((id) => locationPresetsQuery.data.find((p) => p.id === id))
            .filter((p) => p !== undefined)
            .map((p) => ({
                id: p.id,
                name: p.name,
                selected: selected.has(p.id),
            }))
            .sort((a, b) => Number(b.selected) - Number(a.selected));
    }, [presetIds, selectedPresets, locationPresetsQuery.data]);
};

interface LocationDisplayProps {
    location: number[];
    // Presets of the object being displayed.
    presets?: number[];
    // Presets currently being filtered by: shown first and highlighted.
    selectedPresets?: number[];
    allowCopy?: boolean;
    mapLinkParams?: {
        lat?: string;
        lon?: string;
        emissionId?: string;
        siteId?: string;
    };
}

export const LocationDisplay = (props: LocationDisplayProps) => {
    const [locationColumnMode] = useAtom(locationColumnModeAtom);

    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "bottom-start",
        middleware: [flip(), offset({ mainAxis: 4, crossAxis: -5 }), shift()],
        whileElementsMounted: autoUpdate,
    });

    const presets = useLocationPresets(props.presets, props.selectedPresets);

    const showPresets = locationColumnMode === "PRESETS" && !!props.presets;
    const coordinates = formatCoordinates(props.location);

    return (
        // Fixed width so the column doesn't resize when switching modes.
        <div className="flex items-center gap-1 w-56">
            <div className="flex-1 min-w-0">
                {showPresets && presets.length === 0 ? (
                    <span className="text-sm text-[#8a93b0]">—</span>
                ) : (
                    <Popover
                        as="div"
                        ref={refs.setReference}
                        className="min-w-0"
                    >
                        <Popover.Button
                            title={
                                showPresets
                                    ? "Show all presets"
                                    : "Show details"
                            }
                            className={`
                                flex items-center gap-1 min-w-0 max-w-full -ml-[5px] px-1 py-[3px]
                                border border-transparent hover:border-[#d8dde8] hover:bg-white
                                ${showPresets ? "rounded-[14px]" : "rounded-[5px]"}
                            `}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {showPresets ? (
                                <>
                                    <PresetPill
                                        name={presets[0].name}
                                        selected={presets[0].selected}
                                        truncate
                                    />
                                    {presets.length > 1 && (
                                        <span className="flex-none h-6 px-2 rounded-full bg-[#e4f1f8] text-[#0f5a83] text-[12.5px] font-medium inline-flex items-center">
                                            +{presets.length - 1}
                                        </span>
                                    )}
                                </>
                            ) : (
                                <span className="font-mono text-xs tracking-tight whitespace-nowrap overflow-hidden text-ellipsis min-w-0">
                                    {coordinates}
                                </span>
                            )}
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className="flex-none h-2.5 text-[#7b86a8]"
                            />
                        </Popover.Button>
                        <Popover.Panel
                            ref={refs.setFloating}
                            style={floatingStyles}
                            className="z-20 w-[280px] bg-white border border-[#dfe3ec] rounded-lg shadow-[0_10px_28px_rgba(29,43,94,0.16)] p-3 font-normal"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {({ close }) => (
                                <>
                                    {presets.length > 0 && (
                                        <>
                                            <div className="flex items-center mb-2">
                                                <span className="text-xs text-[#6b7699]">
                                                    {presets.length} tag
                                                    {presets.length === 1
                                                        ? ""
                                                        : "s"}
                                                </span>
                                                <button
                                                    type="button"
                                                    title="Close"
                                                    className="ml-auto text-[#6b7699] hover:text-[#1f2d6b]"
                                                    onClick={() => close()}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faXmark}
                                                        className="h-3.5"
                                                    />
                                                </button>
                                            </div>
                                            <div className="flex flex-wrap gap-[5px]">
                                                {presets.map((preset) => (
                                                    <PresetPill
                                                        key={preset.id}
                                                        name={preset.name}
                                                        selected={
                                                            preset.selected
                                                        }
                                                    />
                                                ))}
                                            </div>
                                        </>
                                    )}
                                    <div
                                        className={`flex items-center gap-1 ${presets.length > 0 ? "mt-2.5 pt-2 border-t border-[#eceef3]" : ""}`}
                                    >
                                        <span className="font-mono text-[12.5px] text-[#6b7699]">
                                            {coordinates}
                                        </span>
                                        <span className="ml-auto" />
                                        {props.mapLinkParams && (
                                            <IconButton
                                                title="View on map"
                                                icon={faMap}
                                                onClick={() => {
                                                    const params =
                                                        new URLSearchParams(
                                                            props.mapLinkParams,
                                                        ).toString();
                                                    window.open(
                                                        `${ROUTES.MAP}?${params}`,
                                                        "_blank",
                                                        "noopener,noreferrer",
                                                    );
                                                }}
                                            />
                                        )}
                                        {props.allowCopy && (
                                            <CopyButton
                                                location={props.location}
                                            />
                                        )}
                                    </div>
                                </>
                            )}
                        </Popover.Panel>
                    </Popover>
                )}
            </div>
            {props.allowCopy && <CopyButton location={props.location} />}
        </div>
    );
};
